<template>
  <section id="music" ref="root" class="music">
    <SectionHeading index="01" title="Our Music">
      Latest releases on Coppados. Press play wherever you listen.
    </SectionHeading>

    <div class="platforms">
      <a
        v-for="platform in platforms"
        :key="platform.name"
        :href="platform.url"
        target="_blank"
        rel="noopener"
        class="platform"
        data-reveal
      >
        <span class="roll"><span :data-text="platform.name">{{ platform.name }}</span></span>
        <span class="arrow" aria-hidden="true">↗</span>
      </a>
    </div>

    <div v-if="!loading && releases.length === 0" class="empty-state" data-reveal>
      New music coming soon.
    </div>

    <div v-else-if="releases.length" class="releases" data-reveal>
      <div class="releases__bar">
        <span>Latest releases <span class="releases__total">({{ pad(releases.length) }})</span></span>
        <span class="releases__hint" aria-hidden="true">← Drag →</span>
      </div>

      <div
        ref="viewport"
        class="releases__viewport"
        :class="{ 'is-dragging': dragging, 'is-hovered': hovered }"
        @pointerdown="onDown"
        @pointerenter="onViewportEnter"
        @pointerleave="onViewportLeave"
        @wheel="onWheel"
        @dragstart.prevent
        @click.capture="onClickCapture"
      >
        <div ref="skew" class="releases__skew">
          <div ref="track" class="releases__track">
            <div
              v-for="copy in copies"
              :key="copy"
              ref="sets"
              class="releases__set"
              :aria-hidden="copy > 1 ? 'true' : null"
            >
              <component
                :is="release.link ? 'a' : 'div'"
                v-for="(release, i) in releases"
                :key="`${copy}-${release.id || i}`"
                :href="release.link || null"
                :target="release.link ? '_blank' : null"
                :rel="release.link ? 'noopener' : null"
                :tabindex="copy > 1 ? -1 : null"
                class="release"
                :class="{ 'is-soon': !release.link }"
                draggable="false"
                @pointerenter="onCardEnter($event, release)"
                @pointerleave="onCardLeave"
              >
                <div class="release__media">
                  <div class="release__vinyl" aria-hidden="true">
                    <div class="release__disc">
                      <img class="release__label" :src="release.image_url" alt="" loading="lazy" draggable="false">
                    </div>
                  </div>
                  <div class="release__sleeve">
                    <img :src="release.image_url" :alt="copy > 1 ? '' : release.name" loading="lazy" draggable="false">
                  </div>
                </div>
                <div class="release__meta">
                  <span class="release__index">{{ pad(i + 1) }}</span>
                  <div class="release__names">
                    <h3 class="release__title">{{ release.title }}</h3>
                    <p v-if="release.artist" class="release__artist">{{ release.artist }}</p>
                  </div>
                  <span
                    v-if="release.has_listen_button !== false"
                    class="pill release__cta"
                    :class="{ 'is-disabled': !release.link }"
                  >
                    {{ release.link ? 'Listen' : 'Soon' }}
                    <span v-if="release.link" class="arrow" aria-hidden="true">↗</span>
                  </span>
                </div>
              </component>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/animations/gsap'
import { Marquee } from '@/animations/marquee'
import { setCursorLabel } from '@/animations/cursor'
import { apiUrl } from '@/services/apiConfig'
import motion from '@/mixins/motion'
import SectionHeading from '@/components/ui/SectionHeading.vue'

// "Artista - Título" -> { artist, title }
function splitName(name = '') {
  const clean = name.trim()
  const at = clean.indexOf(' - ')
  if (at === -1) return { title: clean, artist: '' }
  return { artist: clean.slice(0, at).trim(), title: clean.slice(at + 3).trim() }
}

const DRAG_THRESHOLD = 6

export default {
  name: 'TrackSlider',
  components: { SectionHeading },
  mixins: [motion],
  data() {
    return {
      releases: [],
      loading: true,
      error: null,
      copies: 2,
      dragging: false,
      hovered: false,
      platforms: [
        { name: 'Spotify', url: 'https://open.spotify.com/playlist/1Zx2DXeOljqEk8bjJUWD3y' },
        { name: 'SoundCloud', url: 'https://soundcloud.com/coppados/tracks' },
        { name: 'Beatport', url: 'https://www.beatport.com/es/label/coppados/126440' },
        { name: 'YouTube', url: 'https://www.youtube.com/@coppados' }
      ]
    }
  },
  mounted() {
    this.revealContent()
    this.fetchCarouselData()
  },
  beforeDestroy() {
    this.destroyMarquee()
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, '0')
    },
    async fetchCarouselData() {
      try {
        this.loading = true
        this.error = null
        const response = await fetch(apiUrl('/api/carousel'))
        if (!response.ok) throw new Error('Error al cargar los datos del carrousel')
        const items = await response.json()
        this.releases = items.map((item) => ({ ...item, ...splitName(item.name) }))
      } catch (error) {
        console.error('Error fetching carousel:', error)
        this.error = 'No se pudieron cargar los tracks'
        this.releases = []
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.revealContent()
          if (this.releases.length) this.initMarquee()
        })
      }
    },

    // ---------- Cinta infinita ----------

    initMarquee() {
      const reduced = prefersReducedMotion()
      this.marquee = new Marquee(this.$refs.track, -1, 55)
      this.speedFactor = reduced ? 0 : 1
      this.sense = 1
      this.boost = 0
      this.visible = false
      this.lastSkew = 0
      this.skewTo = gsap.quickTo(this.$refs.skew, 'skewX', { duration: 0.6, ease: 'power3.out' })

      this.measure()
      this.resizeObserver = new ResizeObserver(() => this.measure())
      this.resizeObserver.observe(this.$refs.viewport)

      // Scroll de la página: acelera la cinta en el sentido del scroll.
      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.$refs.viewport,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: (self) => { this.visible = self.isActive },
        onUpdate: (self) => {
          if (reduced) return
          this.sense = self.direction
          this.boost = Math.min(Math.abs(self.getVelocity()) * 0.45, 1600)
        }
      })

      this.tick = (_time, deltaMs) => {
        if (!this.visible) return
        const dt = Math.min(deltaMs / 1000, 0.05)
        const ease = 1 - Math.pow(0.002, dt)
        let target = 1
        if (reduced || this.dragging) target = 0
        else if (this.hovered) target = 0.15
        this.speedFactor += (target - this.speedFactor) * ease
        this.boost *= Math.pow(0.03, dt)
        this.marquee.tick(dt, this.sense, this.dragging ? 0 : this.boost, this.speedFactor)

        const skew = reduced ? 0 : Math.round(gsap.utils.clamp(-5, 5, (this.sense * this.boost) / 200) * 100) / 100
        if (skew !== this.lastSkew) {
          this.lastSkew = skew
          this.skewTo(skew)
        }
      }
      gsap.ticker.add(this.tick)
    },
    destroyMarquee() {
      if (this.tick) gsap.ticker.remove(this.tick)
      if (this.scrollTrigger) this.scrollTrigger.kill()
      if (this.resizeObserver) this.resizeObserver.disconnect()
      this.removeDragListeners()
    },
    measure() {
      const set = this.$refs.sets && this.$refs.sets[0]
      if (!set || !this.marquee) return
      const width = set.getBoundingClientRect().width
      if (!width) return
      const needed = Math.max(2, Math.ceil((window.innerWidth * 1.25) / width) + 1)
      if (needed !== this.copies) this.copies = needed
      this.marquee.resize(width)
    },

    // ---------- Arrastre (mouse y touch) con inercia ----------

    onDown(e) {
      if (!this.marquee || (e.pointerType === 'mouse' && e.button !== 0)) return
      this.drag = { lastX: e.clientX, lastT: performance.now(), total: 0, velocity: 0, id: e.pointerId }
      this.marquee.release(0)
      window.addEventListener('pointermove', this.onMove)
      window.addEventListener('pointerup', this.onUp)
      window.addEventListener('pointercancel', this.onUp)
    },
    onMove(e) {
      const drag = this.drag
      if (!drag || e.pointerId !== drag.id) return
      const now = performance.now()
      const dx = e.clientX - drag.lastX
      const dt = Math.max(1, now - drag.lastT)
      drag.lastX = e.clientX
      drag.lastT = now
      drag.total += Math.abs(dx)
      if (!this.dragging && drag.total > DRAG_THRESHOLD) {
        this.dragging = true
        setCursorLabel('Drag')
      }
      if (!this.dragging) return
      this.marquee.nudge(dx)
      drag.velocity = drag.velocity * 0.6 + (dx / dt) * 1000 * 0.4
    },
    onUp() {
      const drag = this.drag
      this.removeDragListeners()
      if (!drag) return
      if (this.dragging) {
        // Si el último movimiento fue hace rato, no hay "tiro".
        const idle = performance.now() - drag.lastT > 80
        this.marquee.release(idle ? 0 : gsap.utils.clamp(-3000, 3000, drag.velocity))
        this.suppressClick = true
        setTimeout(() => { this.suppressClick = false }, 0)
      }
      this.dragging = false
      this.drag = null
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onMove)
      window.removeEventListener('pointerup', this.onUp)
      window.removeEventListener('pointercancel', this.onUp)
    },
    // Un arrastre no debe abrir el link de la tarjeta.
    onClickCapture(e) {
      if (!this.suppressClick) return
      e.preventDefault()
      e.stopPropagation()
      this.suppressClick = false
    },
    // Swipe horizontal del trackpad.
    onWheel(e) {
      if (!this.marquee || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
      e.preventDefault()
      this.marquee.nudge(-e.deltaX)
    },

    // ---------- Hover / cursor ----------

    onViewportEnter(e) {
      if (e.pointerType !== 'mouse') return
      this.hovered = true
      setCursorLabel('Drag')
    },
    onViewportLeave() {
      this.hovered = false
      if (!this.dragging) setCursorLabel('')
    },
    onCardEnter(e, release) {
      if (e.pointerType !== 'mouse' || this.dragging) return
      setCursorLabel(release.link ? 'Listen' : 'Drag')
    },
    onCardLeave() {
      if (this.hovered) setCursorLabel('Drag')
    }
  }
}
</script>

<style scoped>
.music {
  position: relative;
  z-index: 1;
  background: var(--c-bg);
  padding: var(--section-space) 0 calc(var(--section-space) * 0.4);
}

.platforms {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 0 var(--gutter);
  margin-top: 2.5rem;
}

.platform {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.4rem 1.25rem 1.4rem 0;
  border-bottom: 1px solid var(--c-line);
  font-size: var(--fs-h3);
  text-transform: uppercase;
  letter-spacing: -0.01em;
  --roll-color: var(--c-accent);
}

.platform + .platform {
  padding-left: 1.25rem;
  border-left: 1px solid var(--c-line);
}

.platform .arrow {
  font-size: 0.7em;
  color: var(--c-muted);
  transition: transform 0.6s var(--ease-out), color 0.4s var(--ease-out);
}

@media (hover: hover) {
  .platform:hover .arrow {
    color: var(--c-accent);
  }
}

.empty-state {
  padding: 6rem var(--gutter) 0;
  color: var(--c-muted);
  font-size: var(--fs-lead);
}

/* ---------- Cinta de lanzamientos ---------- */

.releases {
  margin-top: clamp(3rem, 2rem + 3vw, 5rem);
}

.releases__bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--gutter) 1.5rem;
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-muted);
}

.releases__total {
  color: var(--c-accent);
}

.releases__viewport {
  overflow: hidden;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  cursor: grab;
}

.releases__viewport.is-dragging {
  cursor: grabbing;
}

.releases__skew {
  will-change: transform;
}

.releases__track {
  display: flex;
  width: max-content;
  will-change: transform;
}

.releases__set {
  display: flex;
  flex-shrink: 0;
}

.release {
  position: relative;
  display: block;
  flex-shrink: 0;
  width: clamp(17rem, 13rem + 16vw, 32rem);
  margin-right: clamp(1rem, 0.5rem + 1.5vw, 2rem);
  -webkit-user-drag: none;
}

.is-dragging .release {
  pointer-events: none;
}

.release__media {
  position: relative;
  aspect-ratio: 16 / 9;
}

/* Portada: el "sobre" que se corre a la izquierda en hover */
.release__sleeve {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  background: var(--c-surface);
  transition: transform 0.9s var(--ease-out), box-shadow 0.9s var(--ease-out);
}

.release__sleeve img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 1.2s var(--ease-out), filter 0.6s var(--ease-out);
  pointer-events: none;
}

/* Vinilo escondido detrás de la portada */
.release__vinyl {
  position: absolute;
  top: 5%;
  right: 3%;
  height: 90%;
  aspect-ratio: 1;
  transition: transform 1s var(--ease-out);
}

/* Brillo fijo: no gira con el disco */
.release__vinyl::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  background: conic-gradient(
    from 20deg,
    rgba(255, 255, 255, 0) 0deg,
    rgba(255, 255, 255, 0.14) 35deg,
    rgba(255, 255, 255, 0) 75deg,
    rgba(255, 255, 255, 0) 180deg,
    rgba(255, 255, 255, 0.1) 215deg,
    rgba(255, 255, 255, 0) 255deg
  );
}

.release__disc {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background:
    repeating-radial-gradient(circle at center, #0c0c0c 0 1px, #191919 1px 3px),
    #111;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.55), inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  animation: vinyl-spin 2.4s linear infinite;
  animation-play-state: paused;
}

/* Agujero central */
.release__disc::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4%;
  height: 4%;
  border-radius: 50%;
  background: #050505;
  transform: translate(-50%, -50%);
}

.release__label {
  position: absolute;
  top: 32%;
  left: 32%;
  width: 36%;
  height: 36%;
  border-radius: 50%;
  object-fit: cover;
  pointer-events: none;
}

@keyframes vinyl-spin {
  to {
    transform: rotate(360deg);
  }
}

.release__meta {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 1rem;
  padding-top: 1rem;
}

.release__index {
  font-size: var(--fs-small);
  color: var(--c-accent);
  letter-spacing: 0.1em;
  padding-top: 0.3em;
}

.release__title {
  font-size: clamp(1rem, 0.85rem + 0.6vw, 1.45rem);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1.05;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.release__artist {
  margin-top: 0.35rem;
  color: var(--c-muted);
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.release__cta {
  white-space: nowrap;
  padding: 0.8em 1.2em;
}

@media (hover: hover) {
  .releases__viewport.is-hovered:not(.is-dragging) .release:not(:hover) .release__sleeve img {
    filter: grayscale(1) brightness(0.55);
  }

  .release:hover {
    z-index: 2;
  }

  .release:hover .release__sleeve {
    transform: translateX(-8%);
    box-shadow: 18px 0 40px rgba(0, 0, 0, 0.55);
  }

  .release:hover .release__sleeve img {
    transform: scale(1.05);
  }

  .release:hover .release__vinyl {
    transform: translateX(32%);
  }

  .release:hover .release__disc {
    animation-play-state: running;
  }

  .release:hover .release__cta {
    color: #fff;
    border-color: var(--c-accent);
  }

  .release:hover .release__cta::before {
    transform: translateY(0);
  }
}

@media (max-width: 1023px) {
  .platforms {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .platform:nth-child(3) {
    padding-left: 0;
    border-left: 0;
  }

  .releases__hint {
    display: none;
  }

  .release__meta {
    grid-template-columns: auto 1fr;
  }

  .release__cta {
    grid-column: 2;
    justify-self: start;
  }
}

@media (max-width: 520px) {
  .platform {
    font-size: 1.15rem;
  }
}
</style>
